import React from "react";
import { UserX } from "lucide-react";
import { toast } from "sonner";

import { useSetAgentOwner } from "@/shared/api/agents";
import { memberName, personName, useMembers } from "@/shared/api/members";
import { ListCard, ListRow, ListTile } from "@/shared/components/ListCard";
import { Chip, GhostButton } from "@/shared/components/ui-kit";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";

import type { PersonRef } from "@/shared/types";

/* Who approves the agent's skills (backend/modules/agents/README.md, "The owner"). An admin can hand
   it to any active member; with nobody, the agent takes no skills until one is chosen. */

const initials = (name: string) =>
  name.split(/[\s@._-]+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

export default function SkillOwner({ agentId, owner, isYou, canChange }: {
  agentId: string;
  owner: PersonRef | null;
  isYou: boolean;
  /** Admins only: the API refuses everyone else. */
  canChange: boolean;
}) {
  return (
    <ListCard label="Owner" action={canChange && <OwnerPicker agentId={agentId} owner={owner} />}>
      {owner ? (
        <ListRow
          lead={<ListTile><span className="text-[11px] font-semibold">{initials(personName(owner))}</span></ListTile>}
          title={
            <span className="inline-flex items-center gap-1.5 max-w-full">
              <span className="truncate">{personName(owner)}</span>
              {isYou && <Chip>You</Chip>}
            </span>
          }
          desc="Approves the skills this agent takes"
        />
      ) : (
        <ListRow
          lead={<ListTile className="bg-[var(--warn-bg)] text-[var(--warn-text)]"><UserX className="w-[14px] h-[14px]" strokeWidth={1.75} /></ListTile>}
          title="No owner"
          desc="An admin assigns one before this agent can take skills."
        />
      )}
    </ListCard>
  );
}

function OwnerPicker({ agentId, owner }: { agentId: string; owner: PersonRef | null }) {
  const { data: members, isPending } = useMembers();
  const setOwner = useSetAgentOwner();
  const active = (members ?? []).filter((m) => m.status === "active");
  const choose = (userId: string) => {
    const member = active.find((m) => m.user_id === userId);
    if (!member || userId === owner?.id) return;
    setOwner.mutate({ id: agentId, userId }, { onSuccess: () => toast.success(`${memberName(member)} owns this agent now`) });
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <GhostButton className="h-7 px-2.5" disabled={setOwner.isPending}>{owner ? "Change" : "Assign owner"}</GhostButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 max-h-[320px] overflow-y-auto p-1 rounded-[12px] bg-[var(--surface)] border border-[var(--hairline-strong)] shadow-menu">
        <DropdownMenuLabel className="px-2 pt-1.5 pb-1 text-[11.5px] font-medium text-[var(--text-tertiary)]">Who approves its skills</DropdownMenuLabel>
        {isPending && <div className="px-2 py-[5px] text-[12.5px] text-[var(--text-quaternary)]">Loading members…</div>}
        <DropdownMenuRadioGroup value={owner?.id ?? ""} onValueChange={choose}>
          {active.map((m) => (
            <DropdownMenuRadioItem key={m.user_id} value={m.user_id} className="px-2 py-[5px] pl-8 rounded-[8px] text-[13px] cursor-pointer focus:bg-[var(--accent-hover)]">
              <span className="min-w-0">
                <span className="block truncate text-[var(--text-primary)]">{memberName(m)}</span>
                {memberName(m) !== m.email && <span className="block truncate text-[11.5px] text-[var(--text-tertiary)]">{m.email}</span>}
              </span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
